import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import './HouseShowcase.css';
import '../ProductSelector/ProductSelector.css';
import { houseModels } from '../../data/houses';

const HouseShowcase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedProduct, setSelectedProduct] = useState('5-marla');
  const [visibleItems, setVisibleItems] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const itemRefs = useRef([]);

  const products = [
    { id: '5-marla', name: 'Classic Villas 5-Marla House', value: '5-marla' },
    { id: '10-marla', name: 'Classic Villas 10-Marla House', value: '10-marla' }
  ];

  const handleProductSelect = (productValue) => {
    setSelectedProduct(productValue);

    // Scroll to showcase section after selection
    setTimeout(() => {
      const showcaseSection = document.querySelector('.house-showcase-content');
      if (showcaseSection) {
        showcaseSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Auto-select filter from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get('filter');

    if (filter === '5-marla') {
      // Auto-select 5 Marla product
      setSelectedProduct('5-marla');
    } else if (filter === '10-marla') {
      // Auto-select 10 Marla product
      setSelectedProduct('10-marla');
    }
  }, [location.search]);

  // Filter houses based on selected product
  // When no filter is selected, show only 4 products:
  // 2 of 5 Marla and 2 of 10 Marla (by order in houseModels).
  const filteredHouses = useMemo(() => {
    const fiveMarlaHouses = houseModels.filter(
      (house) =>
        house.productCategory === '5-marla-classic' ||
        house.productCategory === '5-marla-modern'
    );
    const tenMarlaHouses = houseModels.filter(
      (house) =>
        house.productCategory === '10-marla-classic' ||
        house.productCategory === '10-marla-modern'
    );

    if (!selectedProduct) {
      // Show at most 2 of each marla type
      return [
        ...fiveMarlaHouses.slice(0, 2),
        ...tenMarlaHouses.slice(0, 2),
      ];
    }

    if (selectedProduct === '5-marla') {
      return fiveMarlaHouses.slice(0, 2);
    }

    if (selectedProduct === '10-marla') {
      return tenMarlaHouses.slice(0, 2);
    }

    return [];
  }, [selectedProduct]);

  useEffect(() => {
    // Reset visible items when filtered houses change
    setVisibleItems([]);
    itemRefs.current = [];

    if (filteredHouses.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    // Use setTimeout to ensure refs are set after render
    setTimeout(() => {
      const currentRefs = itemRefs.current;
      currentRefs.forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      const currentRefs = itemRefs.current;
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [filteredHouses]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show message if no houses match the selected product
  if (filteredHouses.length === 0) {
    return (
      <div className="house-showcase">
        <div className="no-houses-found">
          <h2>No Houses Available</h2>
          <p>No houses found for the selected product. Please try another option.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="house-showcase">
      <Navbar />
      <div className="product-selector">
        <div className="product-selector-container">
          <h2 className="product-selector-title">Browse by Category</h2>
          <div className="product-options">
            {products.map((product) => (
              <button
                key={product.id}
                className={`product-option ${selectedProduct === product.value ? 'active' : ''}`}
                onClick={() => handleProductSelect(product.value)}
              >
                <span className="product-option-text">{product.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="house-showcase-content">
        {filteredHouses.map((house, index) => (
          <div
            key={house.id}
            ref={(el) => (itemRefs.current[index] = el)}
            data-index={index}
            className={`showcase-item ${house.backgroundType === 'scenic' ? 'scenic-bg' : 'white-bg'} ${visibleItems.includes(String(index)) ? 'visible' : ''
              }`}
          >
            <div className="showcase-section-id">{house.name}</div>
            <div className="showcase-gradient-overlay"></div>

            <div className="showcase-content">
              <div className="showcase-image-container">
                <div className="image-wrapper">
                  <img
                    src={house.image}
                    alt={house.title}
                    className="showcase-house-image"
                    loading="lazy"
                  />
                  <div className="image-overlay"></div>
                </div>
              </div>

              <div className="showcase-info">
                <div className="showcase-info-content">
                  <div className="showcase-badge">{house.type}</div>

                  <h2 className="showcase-title">{house.title}</h2>

                  <p className="showcase-description">{house.description}</p>

                  <div className="showcase-features">
                    <div className="feature-item">
                      <span className="feature-icon">🏠</span>
                      <span className="feature-text">Spacious Layout</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✨</span>
                      <span className="feature-text">Premium Finishes</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">🌿</span>
                      <span className="feature-text">Modern Design</span>
                    </div>
                  </div>

                  <div className="showcase-pricing-section">
                    <div className="pricing-row">
                      <span className="pricing-label">Starting Price</span>
                      <span className="pricing-amount">${house.startingPrice.toLocaleString()}</span>
                    </div>
                    <div className="pricing-row">
                      <span className="pricing-label">Price as Shown</span>
                      <span className="pricing-amount">${house.priceAsShown.toLocaleString()}</span>
                    </div>
                  </div>

                  <button
                    className="showcase-select-button"
                    onClick={() => navigate(`/configure/${house.id}`)}
                  >
                    <span className="button-text">Select</span>
                    <span className="button-arrow">→</span>
                    <div className="button-ripple"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        className={`back-to-top-button ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <span className="back-to-top-icon">↑</span>
        <span className="back-to-top-text">Back to top</span>
      </button>
    </div>
  );
};

export default HouseShowcase;

